import { observable, action, computed, ObservableMap } from "mobx";
import { LicenseAddedToDatabase, LicenseDeletedFromDatabase } from "../StateEvents";
import { ISODateTimeString, UUID } from "./DatabaseTypes";
import { ExtensionSettingsAccessor } from "./DbExtensionSettings";
import { DbLicensesTable } from "./DbLicensesTable";
import { LicensePermissions } from "./LicensePermissions";
import { License } from "./License";
import { throwIfSignedLicenseDoesNotHaveValidSignature } from "./LicenseValidation";

export class LicenseCache extends LicensePermissions {
	validLicensesByUuid: ObservableMap<UUID, License>;

	@computed get activeClientName() {
		const {activeClientName} = this.extensionsSettings.settings;
		if (activeClientName && this.validClientNameSet.has(activeClientName)) {
			return activeClientName;
		}
		return this.nonExpiredLicensesForAllClientsOrderedMostRecentlyIssuedFirst[0]?.clientName;
	};

	constructor(protected extensionsSettings: ExtensionSettingsAccessor, prefetchedLicenses: License[]) {
		super();
		this.validLicensesByUuid = observable.map<UUID, License>(prefetchedLicenses.map(license => [license.licenseUuid, license] as const));
		LicenseDeletedFromDatabase.addListener(async (message) => {
			const {licenseUuid} = message;
			this.delete({licenseUuid});
		});
		LicenseAddedToDatabase.addListener(async (message) => {
			const signedLicense = message.signedLicense;
			try {
				const validatedLicense = await throwIfSignedLicenseDoesNotHaveValidSignature(signedLicense);
				this.add(validatedLicense);
			} catch (error) {
				console.error("Failed to import license:", error);
			}
		});
		this.validLicensesByUuid = observable.map<UUID, License>(prefetchedLicenses.map(license => [license.licenseUuid, license] as const));
	}


	add = action((...licenses: License[]) => {
		for (const license of licenses) {
			this.validLicensesByUuid.set(license.licenseUuid, license);
		}
	});

	delete = action ( ({licenseUuid}: {licenseUuid: UUID}) => {
		this.validLicensesByUuid.delete(licenseUuid);
	});

	@computed get nonExpiredLicensesForAllClientsOrderedMostRecentlyIssuedFirst() {
		const now = ISODateTimeString();
		return [...this.validLicensesByUuid.values()].filter(license => license.whenExpiresIso > now)
			.sort((a, b) => b.whenIssuedIso.localeCompare(a.whenIssuedIso));
	}
	@computed get nonExpiredLicensesForAllClients() { return this.nonExpiredLicensesForAllClientsOrderedMostRecentlyIssuedFirst; }
	@computed get isLicensed() {
		return this.nonExpiredLicensesForAllClients.length > 0;
	}

	@computed get validClientNameSet(): Set<string> {
		return new Set<string>(
			this.nonExpiredLicensesForAllClientsOrderedMostRecentlyIssuedFirst.map(license => license.clientName)
			.sort((a, b) => a.localeCompare(b))
		);
	}

	@computed get validClientNames() {
		return [...this.validClientNameSet]
	}

	@computed get validNonExpiredLicenses() {
		return this.nonExpiredLicensesForAllClientsOrderedMostRecentlyIssuedFirst.filter(license => license.clientName === this.activeClientName);
	}

	setActiveClientName = (clientName: string) => {
		ExtensionSettingsAccessor.updateSetting('activeClientName', clientName);
	};

	static #instancePromise: Promise<LicenseCache> | undefined;

	static #create = async () => {
		const [extensionSettings, validLicenses] = await Promise.all([
			ExtensionSettingsAccessor.instance,
			DbLicensesTable.loadValidLicenses()
		]);
		return new LicenseCache(extensionSettings, validLicenses);
	}

	static get instancePromise() {
		return LicenseCache.#instancePromise ?? (LicenseCache.#instancePromise = LicenseCache.#create());
	}
}
